// src/components/copiloto/Copiloto.js — a barra de comando e o painel do Copiloto.
//
// Out/26 (Cetto Copiloto, fases 2 e 3). Fiel ao design do Claude
// Design ("Cetto Copiloto.dc.html"): uma barra discreta no topo do
// dashboard que, ao receber um pedido, EXPANDE inline (não é modal nem
// drawer — o supervisor vê a resposta junto dos cards).
//
// Diferenças deliberadas do design, todas por causa da fase:
//   - Os chips e os exemplos do placeholder pedem só o que já existe. O
//     design cita a pasta LeadsAds e a etiqueta Quente, que não existem em
//     toda empresa: "Agendar disparo" e "Etiquetar quem respondeu"
//     PREENCHEM a barra pra pessoa completar, em vez de enviar um pedido
//     com nomes que talvez não existam.
//   - O card de agendamento (CopilotoAcao) só grava no clique em
//     "Confirmar". O de etiqueta/pasta (CopilotoOrganizar, fase 4) chega
//     já aplicado até 50 contatos, com Desfazer por 10s — acima disso, ele
//     também pede confirmação antes.
//   - O card de relatório desenha os números a partir do que o BACKEND
//     calculou (ver CopilotoRelatorio), não do texto do modelo.
//   - A linha de meta mostra o consumo de tokens: a IA é do cliente
//     (BYOK) e o documento pede o custo visível.
//
// Quem vê: só SUPERVISOR/ADMIN (o AgentDash decide; o backend recusa o
// resto com 403). Empresa sem chave vê a barra desabilitada com
// "Conectar sua IA" → /automacao, sem erro no console.
//
// Out/26 (fase 3 da Agenda): a MESMA barra entra na Agenda
// (`variante="agenda"`), com sugestões de agenda e o tema dela (as cores de
// tema.js viram as da Agenda dentro de `.cp-tema-agenda`). A sessão é a
// mesma do dashboard — é o mesmo Copiloto. A Agenda usa o `ref` pra
// preencher a barra e reler a sessão quando a proposta é confirmada nos
// Detalhes, e `aoResponder`/`aoMudar` pra redesenhar a agenda.
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FiFileText, FiCalendar, FiTag, FiArrowUp, FiArrowUpRight, FiRotateCcw, FiChevronUp, FiChevronDown, FiClock, FiList } from 'react-icons/fi';
import api from '../../services/Api';
import CopilotoAcao from './CopilotoAcao';
import CopilotoCompromisso from './CopilotoCompromisso';
import CopilotoOrganizar from './CopilotoOrganizar';
import Faisca from './Faisca';
import CopilotoRelatorio from './CopilotoRelatorio';
import { C, ACENTO, FONT_NUM, FONT_TXT, T } from './tema';

// `preencher` = vai pra barra pra pessoa completar, em vez de enviar.
const CHIPS = [
  { label: 'Agendar disparo', Icone: FiCalendar, preencher: 'agende o envio do template ' },
  { label: 'Relatório do mês', Icone: FiFileText, text: 'me dê um relatório de como foi o atendimento da minha empresa esse mês' },
  { label: 'Etiquetar quem respondeu', Icone: FiTag, preencher: 'etiquete quem respondeu ao template ' },
];

const PLACEHOLDERS = [
  'Peça algo ao Copiloto…',
  'Experimente: “agende o follow up para a pasta LeadsAds amanhã às 15h”',
  'Experimente: “como foi o atendimento este mês?”',
  'Experimente: “etiquete como Quente quem respondeu ao follow up”',
  'Experimente: “quais disparos estão agendados para esta semana?”',
];

// Na Agenda (fase 3), com o nome que a empresa dá à consulta ("uma
// reunião", "um atendimento").
function daAgenda(nome) {
  const singular = (nome?.singular || 'Consulta').toLowerCase();
  const artigo = nome?.genero === 'm' ? 'um' : 'uma';
  return {
    chips: [
      { label: `Marcar ${singular}`, Icone: FiCalendar, preencher: `marque ${artigo} ${singular} com ` },
      { label: 'O que tenho amanhã', Icone: FiList, text: 'o que tenho amanhã?' },
      { label: 'Horários livres', Icone: FiClock, preencher: 'horários livres na ' },
    ],
    placeholders: [
      'Peça algo ao Copiloto…',
      `Experimente: “marque ${artigo} ${singular} com Carlos Mendes quinta às 10h”`,
      'Experimente: “o que tenho amanhã?”',
      'Experimente: “horários livres na quinta”',
    ],
  };
}

// O que aparece enquanto a IA trabalha. É um palpite pelo texto do
// pedido — o backend não transmite progresso —, então as frases dizem o
// que PROVAVELMENTE está acontecendo, sem prometer datas.
function rotuloDeEspera(texto, variante) {
  const t = (texto || '').toLowerCase();
  if (variante === 'agenda') {
    if (/(livre|hor[áa]rios? dispon)/.test(t)) return 'Procurando horários livres…';
    if (/(pediu|conversa|falou|disse|mensagem)/.test(t)) return 'Lendo a conversa do contato…';
    if (/(marqu|marcar|remarc|cancel|mude|mova|troque)/.test(t)) return 'Conferindo a agenda e o contato…';
    if (/(tenho|agenda|amanh|hoje|semana|compromisso)/.test(t)) return 'Consultando a agenda…';
  }
  if (/(agend|program|dispar|envi)/.test(t) && !/(quais|lista|mostr)/.test(t)) {
    return 'Resolvendo pasta, template e data…';
  }
  if (/(etiquet|mova |mover|coloque|tire )/.test(t)) return 'Separando os contatos…';
  if (/template/.test(t)) return 'Consultando os templates na Meta…';
  if (/(disparo|agendad|lembrete)/.test(t)) return 'Consultando os disparos agendados…';
  if (/(relat|como foi|resumo|m[ée]tric|desempenho|semana|m[êe]s|convers|tempo m[ée]dio|sa[úu]de|volume)/.test(t)) {
    return 'Consultando as métricas do período…';
  }
  if (/(contato|pasta|etiqueta|respons[áa]vel|lead)/.test(t)) return 'Buscando contatos no CRM…';
  return 'Pensando…';
}

function fmtTokens(n) {
  if (!n) return '';
  if (n < 1000) return `${n} tokens`;
  return `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil tokens`;
}

let seq = 0;
const idLocal = () => `local-${Date.now()}-${++seq}`;

function doServidor(m) {
  if (m.role === 'user') return { id: m.id, kind: 'user', text: m.text };
  return {
    id: m.id,
    kind: m.acoes?.length ? 'action' : (m.relatorios?.length ? 'report' : 'text'),
    text: m.text,
    relatorios: m.relatorios || [],
    acoes: m.acoes || [],
    consultas: m.consultas || [],
    tokens: m.tokens,
    truncated: !!m.truncated,
  };
}

// Erro → título + frase. O `detail` do backend já vem em português e é
// específico (prazo, chave, crédito); o título só classifica.
function erroDaChamada(e) {
  const status = e?.response?.status;
  const detalhe = e?.response?.data?.detail;
  if (!e?.response) {
    return { titulo: 'Sem conexão', detalhe: 'Não foi possível falar com o servidor. Confira sua internet.' };
  }
  if (status === 504) return { titulo: 'Sem resposta da IA', detalhe };
  if (status === 429) {
    return {
      titulo: 'Muitos pedidos seguidos',
      detalhe: 'São no máximo 20 pedidos por minuto. Aguarde um instante e tente de novo.',
    };
  }
  if (status === 401) return { titulo: 'Chave de IA recusada', detalhe, semChave: true };
  if (status === 402) return { titulo: 'IA não conectada', detalhe, semChave: true };
  return { titulo: 'A IA não respondeu', detalhe: detalhe || 'Tente de novo em instantes.' };
}

const animacoes = {
  '@keyframes cpShimmer': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } },
  '@keyframes cpIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
  '@media (prefers-reduced-motion: reduce)': { '& *': { animation: 'none !important' } },
};

const focoVisivel = { '&:focus-visible': { outline: `2px solid ${ACENTO.base}`, outlineOffset: 2 } };

const botaoCabecalho = {
  display: 'flex', alignItems: 'center', gap: '5px', height: 26, px: 1, borderRadius: '6px',
  border: '1px solid transparent', bgcolor: 'transparent', color: C.text2, fontFamily: FONT_TXT,
  fontSize: '10.56px', fontWeight: 600, cursor: 'pointer',
  '&:hover': { color: C.text, bgcolor: T.hover }, ...focoVisivel,
};

const chipSx = {
  display: 'flex', alignItems: 'center', gap: '6px', p: '4px 10px', borderRadius: '20px',
  border: `1px solid ${C.border}`, bgcolor: 'transparent', color: C.text2, fontFamily: FONT_TXT,
  fontSize: '11.2px', fontWeight: 500, cursor: 'pointer', transition: 'all .15s',
  '&:hover': { color: ACENTO.claro, borderColor: 'rgba(145,132,217,0.35)', bgcolor: 'rgba(145,132,217,0.06)' },
  ...focoVisivel,
};

const barraFantasma = (largura) => ({
  height: 9, width: largura, borderRadius: '4px',
  background: `linear-gradient(90deg, ${C.bg3} 0%, ${T.brilho} 45%, ${C.bg3} 90%)`,
  backgroundSize: '200% 100%', animation: 'cpShimmer 1.6s linear infinite',
});

const META_DA_ACAO = { agendamento: '· escrita agendada', compromisso: '· proposta na agenda' };

function Resposta({ m, aoTentarDeNovo, aoMudar, aoVer }) {
  const meta = m.kind === 'thinking' || m.kind === 'error'
    ? ''
    : [
      m.kind === 'action'
        ? (META_DA_ACAO[m.acoes?.[0]?.tipo] || '· escrita reversível')
        : (m.consultas?.length || m.kind === 'report' ? '· leitura' : ''),
      m.tokens ? `· ${fmtTokens(m.tokens)}` : '',
    ].filter(Boolean).join(' ');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', animation: 'cpIn .25s ease' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: 1 }}>
        <Faisca size={12} color={ACENTO.base} />
        <Box component="span" sx={{ fontSize: '10.56px', fontWeight: 600, color: ACENTO.claro, letterSpacing: '0.02em' }}>Copiloto</Box>
        {meta && <Box component="span" sx={{ fontSize: '10.56px', color: C.text3 }}>{meta}</Box>}
      </Box>

      {m.kind === 'thinking' && (
        <Box role="status" sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 520 }}>
          <Box component="span" sx={{ fontSize: '11.2px', color: C.text2 }}>{m.rotulo}</Box>
          <Box sx={barraFantasma('82%')} />
          <Box sx={barraFantasma('64%')} />
          <Box sx={barraFantasma('72%')} />
        </Box>
      )}

      {(m.kind === 'text' || m.kind === 'action') && (
        <Box sx={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(m.text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean).map((p, i) => (
            <Box key={i} sx={{ fontSize: '12.48px', color: C.text, lineHeight: 1.6, textWrap: 'pretty', whiteSpace: 'pre-line' }}>{p}</Box>
          ))}
        </Box>
      )}

      {m.kind === 'action' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.25 }}>
          {m.acoes.map((a) => {
            if (a.tipo === 'agendamento') return <CopilotoAcao key={a.acao_id} acao={a} />;
            if (a.tipo === 'compromisso') return <CopilotoCompromisso key={a.acao_id} acao={a} aoMudar={aoMudar} aoVer={aoVer} />;
            return <CopilotoOrganizar key={a.acao_id} acao={a} />;
          })}
        </Box>
      )}

      {m.kind === 'report' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {m.relatorios.map((r, i) => (
            // Quando o modelo consulta mais de um período, o texto vai só
            // no primeiro card — ele já fala dos dois.
            <CopilotoRelatorio key={`${r.periodo.inicio}-${i}`} texto={i === 0 ? m.text : ''} relatorio={r} />
          ))}
        </Box>
      )}

      {m.truncated && (
        <Box sx={{ mt: 1, fontSize: '10.56px', color: C.text2 }}>
          A resposta foi cortada no tamanho máximo. Peça um recorte menor para ver o resto.
        </Box>
      )}

      {m.kind === 'error' && (
        <Box sx={{
          maxWidth: 540, display: 'flex', alignItems: 'stretch', border: `1px solid ${T.vermelhoB('rgba(255,69,96,0.22)')}`,
          borderRadius: '10px', overflow: 'hidden', background: `linear-gradient(90deg, ${T.vermelhoBg('rgba(255,69,96,0.10)')} 0%, transparent 58%)`,
        }}>
          <Box sx={{ width: '2px', bgcolor: C.red, flexShrink: 0, opacity: 0.9 }} />
          <Box sx={{ p: '11px 14px', flex: 1, minWidth: 0 }} role="alert">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5.6px', mb: '4.8px' }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: C.red, boxShadow: '0 0 5px rgba(255,69,96,.45)' }} />
              <Box component="span" sx={{ fontSize: '10.24px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.red, lineHeight: 1.2 }}>
                {m.titulo}
              </Box>
            </Box>
            <Box sx={{ fontSize: '10.88px', color: C.text2, lineHeight: 1.5 }}>{m.detalhe}</Box>
            {!m.semChave && (
              <Box
                component="button" type="button" onClick={() => aoTentarDeNovo(m)}
                sx={{
                  mt: '6px', p: 0, bgcolor: 'transparent', border: 'none', fontFamily: FONT_TXT,
                  fontSize: '10.56px', fontWeight: 600, color: C.red, opacity: 0.88, cursor: 'pointer',
                  '&:hover': { opacity: 1 }, ...focoVisivel,
                }}
              >
                <Box component="span" sx={{ opacity: 0.6, mr: '4.8px' }}>→</Box>Tentar de novo
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const Copiloto = forwardRef(function Copiloto({ variante = 'dashboard', nomeConsulta, aoResponder, aoMudar, aoVer }, ref) {
  const navigate = useNavigate();
  const agenda = variante === 'agenda';
  const { chips: CHIPS_DA_TELA, placeholders: PLACEHOLDERS_DA_TELA } = agenda
    ? daAgenda(nomeConsulta)
    : { chips: CHIPS, placeholders: PLACEHOLDERS };
  const [estado, setEstado] = useState('carregando'); // carregando | pronto | oculto
  const [disponivel, setDisponivel] = useState(false);
  const [conversa, setConversa] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [texto, setTexto] = useState('');
  const [focado, setFocado] = useState(false);
  const [ph, setPh] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef(null);
  const rolagemRef = useRef(null);
  const ehMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

  const vivoRef = useRef(true);
  const enviandoRef = useRef(false);
  const carregar = useCallback(() => api.get('/copilot/session')
    .then((r) => {
      if (!vivoRef.current) return;
      setDisponivel(!!r.data.available);
      setConversa((r.data.messages || []).map(doServidor));
      setEstado('pronto');
    })
    .catch((e) => {
      if (!vivoRef.current) return;
      // 403 = papel sem Copiloto: some sem rastro. Qualquer outra falha
      // (rede, 5xx) mantém a barra utilizável — o comando, se falhar,
      // explica o porquê no próprio painel.
      if (e?.response?.status === 403) { setEstado('oculto'); return; }
      setDisponivel(e?.response?.status !== 402);
      setEstado('pronto');
    }), []);

  useEffect(() => {
    vivoRef.current = true;
    carregar();
    return () => { vivoRef.current = false; };
  }, [carregar]);

  // A Agenda preenche a barra ("Remarcar com o Copiloto") e relê a sessão
  // quando uma proposta é confirmada ou descartada nos Detalhes — o card
  // aqui passa a mostrar o estado novo.
  useImperativeHandle(ref, () => ({
    preencher: (t) => {
      setTexto(t);
      setTimeout(() => {
        const el = inputRef.current;
        if (el) { el.focus(); el.setSelectionRange(t.length, t.length); }
      }, 0);
    },
    recarregar: () => { if (!enviandoRef.current) carregar(); },
  }), [carregar]);

  // Placeholder rotativo: só gira com a barra parada — mudar o texto
  // enquanto a pessoa lê ou digita é distração.
  useEffect(() => {
    if (focado || texto || aberto || !disponivel) return undefined;
    const t = setInterval(() => setPh((p) => (p + 1) % PLACEHOLDERS_DA_TELA.length), 3600);
    return () => clearInterval(t);
  }, [focado, texto, aberto, disponivel, PLACEHOLDERS_DA_TELA.length]);

  // ⌘K / Ctrl+K foca a barra; Esc recolhe o painel.
  useEffect(() => {
    if (estado !== 'pronto') return undefined;
    const aoTeclar = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === 'Escape' && aberto) {
        setAberto(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [estado, aberto]);

  useEffect(() => {
    if (aberto && rolagemRef.current) rolagemRef.current.scrollTop = rolagemRef.current.scrollHeight;
  }, [conversa, aberto]);

  const trocar = (id, novo) => setConversa((c) => c.map((m) => (m.id === id ? novo : m)));

  const executar = async (pedido, idPedido, idResposta) => {
    setEnviando(true);
    enviandoRef.current = true;
    try {
      const { data } = await api.post('/copilot/command', { text: pedido });
      trocar(idPedido, doServidor(data.user_message));
      trocar(idResposta, { ...doServidor(data.message), truncated: !!data.message.truncated });
      if (aoResponder) aoResponder(data.message);
    } catch (e) {
      const erro = erroDaChamada(e);
      if (erro.semChave) setDisponivel(false);
      trocar(idResposta, {
        id: idResposta, kind: 'error', titulo: erro.titulo, detalhe: erro.detalhe,
        semChave: !!erro.semChave, pedido, idPedido,
      });
    } finally {
      setEnviando(false);
      enviandoRef.current = false;
    }
  };

  const enviar = (bruto) => {
    const pedido = (bruto || '').trim();
    if (!pedido || !disponivel || enviando) return;
    const idPedido = idLocal();
    const idResposta = idLocal();
    setConversa((c) => [
      ...c,
      { id: idPedido, kind: 'user', text: pedido },
      { id: idResposta, kind: 'thinking', rotulo: rotuloDeEspera(pedido, variante) },
    ]);
    setTexto('');
    setAberto(true);
    executar(pedido, idPedido, idResposta);
  };

  const tentarDeNovo = (m) => {
    if (enviando) return;
    trocar(m.id, { id: m.id, kind: 'thinking', rotulo: rotuloDeEspera(m.pedido, variante) });
    executar(m.pedido, m.idPedido, m.id);
  };

  const limpar = () => {
    api.delete('/copilot/session').catch(() => {});
    setConversa([]);
    setAberto(false);
    inputRef.current?.focus();
  };

  if (estado !== 'pronto') return null;

  const ativo = disponivel && (focado || aberto);
  const pedidosNaSessao = conversa.filter((m) => m.kind === 'user').length;
  const placeholder = !disponivel
    ? 'Conecte sua chave de IA para usar o Copiloto'
    : aberto ? 'Continue a conversa…' : PLACEHOLDERS_DA_TELA[ph % PLACEHOLDERS_DA_TELA.length];

  return (
    <Box data-screen-label="Copiloto" sx={{ display: 'flex', flexDirection: 'column', gap: '10px', fontFamily: FONT_TXT, ...animacoes }}>
      <Box sx={{
        position: 'relative', bgcolor: C.bg2, borderRadius: '12px',
        border: `1px solid ${ativo ? 'rgba(145,132,217,0.40)' : C.border}`,
        boxShadow: ativo ? T.sombra : 'none',
        transition: 'border-color .25s ease, box-shadow .25s ease',
      }}>
        <Box sx={{
          position: 'absolute', top: '-1px', left: 48, right: 48, height: '1px', pointerEvents: 'none',
          background: `linear-gradient(90deg, transparent, ${ACENTO.base}, transparent)`,
          opacity: !disponivel ? 0 : ativo ? 1 : 0.45, transition: 'opacity .3s ease',
        }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', height: 50, pl: 2, pr: 1 }}>
          <Faisca size={18} color={disponivel ? ACENTO.base : C.text3} />
          <Box
            component="input"
            ref={inputRef}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) enviar(texto); }}
            onFocus={() => setFocado(true)}
            onBlur={() => setFocado(false)}
            disabled={!disponivel}
            placeholder={placeholder}
            maxLength={1000}
            aria-label="Peça algo ao Copiloto"
            sx={{
              flex: 1, minWidth: 0, height: '100%', bgcolor: 'transparent', border: 'none', outline: 'none',
              color: C.text, fontFamily: FONT_TXT, fontSize: '13.6px', fontWeight: 500, letterSpacing: '0.01em',
              opacity: disponivel ? 1 : 0.6, textOverflow: 'ellipsis',
              '&::placeholder': { color: C.text2, opacity: 1 },
            }}
          />
          {disponivel && !texto && (
            <Box aria-hidden="true" sx={{ display: { xs: 'none', sm: 'flex' }, gap: '3px', mr: '6px', flexShrink: 0 }}>
              {[ehMac ? '⌘' : 'Ctrl', 'K'].map((k) => (
                <Box key={k} component="span" sx={{
                  minWidth: 20, height: 20, px: '5px', borderRadius: '5px', border: `1px solid ${T.neutroB}`,
                  bgcolor: C.bg3, color: C.text2, fontFamily: FONT_NUM, fontSize: '10.56px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{k}</Box>
              ))}
            </Box>
          )}
          {disponivel && !!texto && (
            <Box
              component="button" type="button" onClick={() => enviar(texto)} disabled={enviando}
              aria-label="Enviar"
              sx={{
                width: 32, height: 32, borderRadius: '8px', border: `1px solid ${ACENTO.base}`, bgcolor: 'transparent',
                color: ACENTO.claro, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0,
                cursor: enviando ? 'default' : 'pointer', flexShrink: 0, opacity: enviando ? 0.5 : 1,
                transition: 'background .15s', '&:hover': { bgcolor: 'rgba(145,132,217,0.14)' },
                '&:active': { bgcolor: 'rgba(145,132,217,0.24)' }, ...focoVisivel,
              }}
            >
              <FiArrowUp size={15} />
            </Box>
          )}
          {!disponivel && (
            <Box
              component="button" type="button" onClick={() => navigate('/automacao')}
              sx={{
                display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, fontFamily: FONT_TXT,
                fontSize: '11.2px', fontWeight: 700, color: ACENTO.claro, border: `1px solid ${ACENTO.base}`,
                borderRadius: '7px', p: '6px 12px', bgcolor: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'background .15s',
                '&:hover': { bgcolor: 'rgba(145,132,217,0.14)', color: ACENTO.maisClaro }, ...focoVisivel,
              }}
            >
              Conectar sua IA <FiArrowUpRight size={12} />
            </Box>
          )}
        </Box>

        {aberto && (
          <Box sx={{ borderTop: `1px solid ${T.linha('rgba(255,255,255,0.07)')}`, animation: 'cpIn .22s ease' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', p: '10px 10px 0 16px' }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: ACENTO.base }} />
              <Box component="span" sx={{ fontSize: '9.92px', fontWeight: 700, letterSpacing: '0.16em', color: C.text3, textTransform: 'uppercase' }}>
                Copiloto · sessão atual
              </Box>
              <Box sx={{ ml: 'auto', display: 'flex', gap: '4px' }}>
                <Box component="button" type="button" onClick={limpar} disabled={enviando} sx={botaoCabecalho}>
                  <FiRotateCcw size={12} />Limpar
                </Box>
                <Box component="button" type="button" onClick={() => setAberto(false)} sx={botaoCabecalho}>
                  <FiChevronUp size={12} />Recolher
                </Box>
              </Box>
            </Box>
            <Box
              ref={rolagemRef}
              aria-live="polite"
              sx={{
                // Na Agenda o painel divide a tela com o calendário: altura do
                // design (230px), com rolagem.
                maxHeight: { xs: agenda ? '50vh' : '60vh', md: agenda ? 240 : 440 }, overflowY: 'auto', p: '14px 16px 18px',
                display: 'flex', flexDirection: 'column', gap: 2,
                scrollbarWidth: 'thin', scrollbarColor: `${C.bg4} transparent`,
              }}
            >
              {conversa.map((m) => (m.kind === 'user' ? (
                <Box key={m.id} sx={{ display: 'flex', justifyContent: 'flex-end', animation: 'cpIn .2s ease' }}>
                  <Box sx={{
                    maxWidth: '72%', bgcolor: C.bg3, border: `1px solid ${C.border}`, borderRadius: '12px 12px 4px 12px',
                    p: '8px 12px', fontSize: '12.48px', color: C.text, lineHeight: 1.5, overflowWrap: 'anywhere',
                  }}>{m.text}</Box>
                </Box>
              ) : (
                <Resposta key={m.id} m={m} aoTentarDeNovo={tentarDeNovo} aoMudar={aoMudar} aoVer={aoVer} />
              )))}
            </Box>
          </Box>
        )}
      </Box>

      {disponivel && !aberto && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', pl: '4px' }}>
          <Box component="span" sx={{ fontSize: '10.56px', color: C.text3, mr: '4px' }}>Sugestões</Box>
          {CHIPS_DA_TELA.map(({ label, Icone, text, preencher }) => (
            <Box
              key={label} component="button" type="button" sx={chipSx}
              onClick={() => {
                if (!preencher) { enviar(text); return; }
                setTexto(preencher);
                inputRef.current?.focus();
              }}
            >
              <Box component="span" sx={{ display: 'flex', opacity: 0.8 }}><Icone size={12} /></Box>{label}
            </Box>
          ))}
          {pedidosNaSessao > 0 && (
            <Box
              component="button" type="button" onClick={() => setAberto(true)}
              sx={{
                ml: 'auto', display: 'flex', alignItems: 'center', gap: '6px', p: '4px 4px 4px 10px', border: 'none',
                bgcolor: 'transparent', color: ACENTO.claro, fontFamily: FONT_TXT, fontSize: '11.2px', fontWeight: 600,
                cursor: 'pointer', '&:hover': { color: ACENTO.maisClaro }, ...focoVisivel,
              }}
            >
              Retomar sessão
              <Box component="span" sx={{ fontFamily: FONT_NUM, color: C.text2, fontWeight: 500 }}>· {pedidosNaSessao}</Box>
              <FiChevronDown size={12} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
});

export default Copiloto;
